import { useState } from 'react'
import { Box, Typography } from '@mui/material_modern'
import { styled } from '@mui/system'
import { useGetLockedEntitiesQuery } from 'services/lockAccounting'
import DialogModal from 'components/generic/modals/dialog-modal'
import BasicDataGridV2 from 'components/generic/datagrid/BasicDataGridV2'
import StyledLink from 'components/generic/typography/StyledLink.jsx'
import { LOCK_ENTITY_MAPPER } from 'utils/constants'

export interface LockAccountingModalProps {
  open: boolean
  onClose: () => void
  onLock: () => void
  formattedLockAccountingDate: string
}

const GridStyling = styled('div')(({ theme }) => ({
  '& .MuiDataGrid-root': {
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
  },
}))

const LockAccountingModal: React.FC<LockAccountingModalProps> = ({
  onClose,
  onLock,
  formattedLockAccountingDate,
  ...restProps
}) => {
  const [queryOptions, setQueryOptions] = useState({
    filter: {
      lockAccountingDate: formattedLockAccountingDate,
    },
    page: 1,
    pageSize: 10,
  })

  const { data: lockedEntitiesData, isFetching: isFetchingLockedEntities } = useGetLockedEntitiesQuery({
    ...queryOptions,
    filter: JSON.stringify(queryOptions.filter),
  })

  const transformedRows = (lockedEntitiesData?.Items || []).slice().map(entity => ({
    ...entity,
    id: entity.entityId,
    ticketLink: `${LOCK_ENTITY_MAPPER[entity.type]?.baseUrl}${entity.entityId}`,
  }))

  const columns = [
    {
      field: 'displayId',
      headerName: 'ID',
      flex: 1,
      sortable: false,
      renderCell: params => (
        <StyledLink
          to={params?.row?.ticketLink}
          onClick={event => {
            event.preventDefault()
            window.open(params?.row?.ticketLink, '_blank')
          }}
        >
          {params.row.displayId}
        </StyledLink>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      sortable: false,
      valueGetter: ({ row }) => {
        const { type } = row
        return LOCK_ENTITY_MAPPER[type]?.name || type
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      sortable: false,
      valueGetter: ({ row }) => {
        const { type, status } = row
        return LOCK_ENTITY_MAPPER[type]?.statuses?.[status]?.name || status
      },
    },
  ]

  return (
    <Box>
      <DialogModal
        {...restProps}
        title="Lock Accounting Period"
        buttonTitle="Lock Accounting Period"
        onClose={onClose}
        onSave={() => {
          onLock()
          onClose()
        }}
      >
        <Typography variant="body1" mb={2}>
          There are open items that may need reviewing. You may continue locking this accounting period; however, any
          open items will be locked down.
        </Typography>
        <Typography variant="body1" mb={2}>
          Consider reviewing the following open items before locking this accounting period:
        </Typography>
        <GridStyling>
          <BasicDataGridV2
            paginationMode="server"
            rowCount={lockedEntitiesData?.totalResultsCount || 0}
            onPageChange={(newPageNumber: number) =>
              setQueryOptions(prevState => ({ ...prevState, page: newPageNumber + 1 }))
            }
            onPageSizeChange={(newPageSize: number) =>
              setQueryOptions(prevState => ({ ...prevState, pageSize: newPageSize }))
            }
            autoPageSize={false}
            pageSize={queryOptions.pageSize}
            loading={isFetchingLockedEntities}
            pagination
            rows={transformedRows}
            columns={columns}
            disableColumnMenu
            rowsPerPageOptions={[10, 20, 30]}
            hideFooterSelectedRowCount
            onRowClick={params => {
              window.open(params?.row?.ticketLink, '_blank')
            }}
          />
        </GridStyling>
      </DialogModal>
    </Box>
  )
}

export default LockAccountingModal
